package com.swetdot.swetgram.ui.tasks

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import com.swetdot.swetgram.data.tasks.TasksRepository
import com.swetdot.swetgram.data.tasks.DailyStepsAccess
import com.swetdot.swetgram.data.tasks.DailyStepsSource
import com.swetdot.swetgram.data.tasks.UnavailableStepReason
import com.swetdot.swetgram.data.realtime.RealtimeClient
import com.swetdot.swetgram.domain.DailyTaskItem
import com.swetdot.swetgram.domain.DailyTaskKind
import com.swetdot.swetgram.domain.DailyTasks
import com.swetdot.swetgram.domain.TasksProfile
import com.swetdot.swetgram.ui.text.UiText
import com.swetdot.swetgram.ui.text.text
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.collect
import kotlinx.coroutines.flow.filter
import kotlinx.coroutines.launch
import kotlinx.datetime.TimeZone
import swetgram.shared.generated.resources.Res
import swetgram.shared.generated.resources.tasks_error_load
import swetgram.shared.generated.resources.tasks_error_replace
import swetgram.shared.generated.resources.tasks_error_steps_permission
import swetgram.shared.generated.resources.tasks_error_steps_replacement

data class TasksUiState(
    val today: DailyTasks? = null,
    val profile: TasksProfile? = null,
    val loadedOnce: Boolean = false,
    val refreshing: Boolean = false,
    val replacingTaskId: String? = null,
    val syncingSteps: Boolean = false,
    val showStepsConsent: Boolean = false,
    val error: UiText? = null,
)

class TasksViewModel(
    private val tasks: TasksRepository,
    private val stepsSource: () -> DailyStepsSource? = { null },
    private val realtime: RealtimeClient? = null,
) : ViewModel() {
    private val _state = MutableStateFlow(TasksUiState())
    val state: StateFlow<TasksUiState> = _state.asStateFlow()
    private val refreshes = TasksRefreshCoalescer()
    private val timezone: String get() = TimeZone.currentSystemDefault().id

    init {
        viewModelScope.launch {
            val cached = tasks.cachedToday()
            if (cached != null) _state.value = _state.value.copy(today = cached, loadedOnce = true)
            refreshQuietly()
        }
        if (realtime != null) {
            viewModelScope.launch {
                realtime.signals
                    .filter { signal -> signal.refreshesTasks() }
                    .collect { refreshQuietly() }
            }
        }
    }

    fun refresh() = read(announce = true)
    fun refreshQuietly() = read(announce = false)

    private fun read(announce: Boolean) {
        if (!refreshes.tryStart(announce)) {
            // A realtime frame can race the REST response that preceded it. Dropping that frame
            // could leave this device stale until the next manual refresh, so coalesce the burst
            // into exactly one follow-up revalidation after the in-flight read finishes.
            if (announce) _state.value = _state.value.copy(refreshing = true)
            return
        }
        _state.value = _state.value.copy(refreshing = announce, error = null)
        viewModelScope.launch {
            try {
                val today = tasks.today(timezone)
                val profile = runCatching { tasks.profile() }.getOrNull() ?: _state.value.profile
                _state.value = _state.value.copy(today = today, profile = profile, loadedOnce = true)
                syncGrantedSteps(today, profile)
            } catch (cancellation: CancellationException) {
                throw cancellation
            } catch (_: Throwable) {
                _state.value = _state.value.copy(
                    loadedOnce = true,
                    error = Res.string.tasks_error_load.text(),
                )
            } finally {
                _state.value = _state.value.copy(refreshing = false)
                refreshes.complete()?.let(::read)
            }
        }
    }

    fun replace(task: DailyTaskItem) {
        val today = _state.value.today ?: return
        if (!today.replacementAvailable || _state.value.replacingTaskId != null || task.completed) return
        _state.value = _state.value.copy(replacingTaskId = task.id, error = null)
        viewModelScope.launch {
            try {
                _state.value = _state.value.copy(today = tasks.replace(task, today.version))
            } catch (cancellation: CancellationException) {
                throw cancellation
            } catch (_: Throwable) {
                _state.value = _state.value.copy(error = Res.string.tasks_error_replace.text())
            } finally {
                _state.value = _state.value.copy(replacingTaskId = null)
            }
        }
    }

    fun requestStepsAccess() {
        if (_state.value.syncingSteps) return
        _state.value = _state.value.copy(showStepsConsent = true, error = null)
    }

    fun dismissStepsConsent() {
        _state.value = _state.value.copy(showStepsConsent = false)
        replaceUnavailableStep(UnavailableStepReason.PERMISSION_DENIED)
    }

    fun confirmStepsAccess() {
        if (_state.value.syncingSteps) return
        _state.value = _state.value.copy(showStepsConsent = false, syncingSteps = true, error = null)
        viewModelScope.launch {
            try {
                val source = stepsSource()
                if (source == null) {
                    replaceUnavailableStepNow(UnavailableStepReason.PROVIDER_UNAVAILABLE)
                    return@launch
                }
                when (source.requestAccess()) {
                    DailyStepsAccess.GRANTED -> {
                        var profile = tasks.profile()
                        if (!profile.stepTasksEnabled) {
                            profile = tasks.updatePreferences(
                                profile = profile,
                                timezone = timezone,
                                stepTasksEnabled = true,
                            )
                        }
                        _state.value = _state.value.copy(profile = profile)
                        val total = source.readToday()
                        val today = _state.value.today
                        if (total != null && today != null && total.localDate == today.localDate) {
                            _state.value = _state.value.copy(today = tasks.reportSteps(total))
                        }
                    }
                    DailyStepsAccess.PERMISSION_REQUIRED -> {
                        replaceUnavailableStepNow(UnavailableStepReason.PERMISSION_DENIED)
                    }
                    DailyStepsAccess.UNAVAILABLE -> {
                        replaceUnavailableStepNow(UnavailableStepReason.PROVIDER_UNAVAILABLE)
                    }
                }
            } catch (cancellation: CancellationException) {
                throw cancellation
            } catch (_: Throwable) {
                _state.value = _state.value.copy(
                    error = Res.string.tasks_error_steps_permission.text(),
                )
            } finally {
                _state.value = _state.value.copy(syncingSteps = false)
            }
        }
    }

    private suspend fun syncGrantedSteps(today: DailyTasks, profile: TasksProfile?) {
        if (profile?.stepTasksEnabled != true || today.tasks.none { it.kind == DailyTaskKind.STEPS && !it.completed }) {
            return
        }
        val source = stepsSource()
        if (source == null) {
            replaceUnavailableStepNow(UnavailableStepReason.PROVIDER_UNAVAILABLE)
            return
        }
        when (source.access()) {
            DailyStepsAccess.GRANTED -> Unit
            DailyStepsAccess.PERMISSION_REQUIRED -> {
                replaceUnavailableStepNow(UnavailableStepReason.PERMISSION_REVOKED)
                return
            }
            DailyStepsAccess.UNAVAILABLE -> {
                replaceUnavailableStepNow(UnavailableStepReason.PROVIDER_UNAVAILABLE)
                return
            }
        }
        val total = source.readToday() ?: return
        if (total.localDate != today.localDate) return
        try {
            _state.value = _state.value.copy(today = tasks.reportSteps(total))
        } catch (cancellation: CancellationException) {
            throw cancellation
        } catch (_: Throwable) {
            // The visible server state remains valid; a pull-to-refresh can retry the aggregate.
        }
    }

    private fun replaceUnavailableStep(reason: UnavailableStepReason) {
        if (_state.value.syncingSteps) return
        _state.value = _state.value.copy(syncingSteps = true, error = null)
        viewModelScope.launch {
            try {
                replaceUnavailableStepNow(reason)
            } finally {
                _state.value = _state.value.copy(syncingSteps = false)
            }
        }
    }

    private suspend fun replaceUnavailableStepNow(reason: UnavailableStepReason) {
        val today = _state.value.today ?: return
        val stepTask = today.tasks.firstOrNull { it.kind == DailyTaskKind.STEPS && !it.completed } ?: return
        try {
            val updated = tasks.replaceUnavailableStep(stepTask, today.version, reason)
            val profile = runCatching { tasks.profile() }.getOrNull() ?: _state.value.profile
            _state.value = _state.value.copy(today = updated, profile = profile)
        } catch (cancellation: CancellationException) {
            throw cancellation
        } catch (_: Throwable) {
            _state.value = _state.value.copy(
                error = Res.string.tasks_error_steps_replacement.text(),
            )
        }
    }

    fun dismissError() { _state.value = _state.value.copy(error = null) }
}

/**
 * Collapses a burst of realtime/reconnect hints into one follow-up read without dropping the fact
 * that something changed while a request was already in flight.
 */
internal class TasksRefreshCoalescer {
    private var running = false
    private var queued = false
    private var queuedAnnounced = false

    fun tryStart(announce: Boolean): Boolean {
        if (!running) {
            running = true
            return true
        }
        queued = true
        queuedAnnounced = queuedAnnounced || announce
        return false
    }

    /** Returns the UI-announcement mode for one queued follow-up, or null when fully idle. */
    fun complete(): Boolean? {
        running = false
        if (!queued) return null
        val announce = queuedAnnounced
        queued = false
        queuedAnnounced = false
        return announce
    }
}
